import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createAndInviteUserRequest } from "../../../../actions";
import { UserGroupDescription, UserGroupLabel, UserGroups } from "../../../../enums/user-groups";
import { IRootReducer } from "../../../../reducers";
import {
  CleanPhoneNumberUtil,
  ValidateEmailUtil,
  ValidatePhoneNumberUtil,
} from "../../../../utils/string-utils";

import { useAuthContext } from "../../../../core/auth-context";
import { AgencyCode, AgencyId, JogId } from "../../../../reducers/states/agency";
import ViewEditSelect from "../../../common/view-edit-select";
import { LegacyAgencyGroup } from "../../../../reducers/states/agency/agencies";
import { getAgencyJogIds, Jogs } from "../../../../reducers/states/agency/jogs";

export const getJogId = (agencyId: AgencyId): { jogId: JogId, isReadOnly: boolean } => {
  // todo: jcavner: Special casing KCM for now. But would like to do this differently, based on properties of Agencies/JOGs
  const jogIds = agencyId === AgencyCode.KCM
    ? [JogId.SeattleArea]
    : [...getAgencyJogIds(agencyId)?.values() || [JogId.Other]];
  const jogId = jogIds.length === 1 ? jogIds[0] : JogId.Other;
  const isReadOnly = jogIds?.length < 2;

  return {
    jogId,
    isReadOnly,
  }
}

interface InviteUserProps {
  props: {
    setInvitedEmail: (invitedEmail: string) => void;
  };
}

const InviteUser: FC<InviteUserProps> = ({ props }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [agency, setAgency] = useState("");
  const [jogId, setJogId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [userRoles, setUserRoles] = useState([UserGroups.VCC_BASIC as string]);
  const [inviteBtnDisabled, setInviteBtnDisabled] = useState(true);
  const [agencyReadonly, setAgencyReadOnly] = useState(false);
  const [jogReadonly, setJogReadOnly] = useState(true);

  const dispatch = useDispatch();
  const store = useSelector((state: IRootReducer) => state.adminReducer);
  const { authState } = useAuthContext();

  useEffect(() => {
    // Disable invite if phone number is invalid. Blank is ok.
    if (phoneNumber !== "" && !ValidatePhoneNumberUtil(phoneNumber)) {
      setInviteBtnDisabled(true);
      return;
    }

    // Disable invite if email is blank or invalid
    if (email !== "" && !ValidateEmailUtil(phoneNumber)) {
      setInviteBtnDisabled(true);
      return;
    }

    // Disable invite if any required field is blank
    setInviteBtnDisabled(
      email === "" ||
      firstName === "" ||
      lastName === "" ||
      agency === "" ||
      jogId === "" || jogId === JogId.Other
    );
  }, [agency, jogId, email, firstName, lastName, phoneNumber]);

  const handleOpen = () => {
    if (authState.userGroups.toString().includes(UserGroups.AGENCY_ADMIN)
      && !authState.userGroups.toString().includes(UserGroups.SITE_ADMIN)) {
      setAgency(store.userAgency);
      setAgencyReadOnly(true);
    }

    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
    setEmail("");
    setAgency("");
    setFirstName("");
    setLastName("");
    setPhoneNumber("");
    setAgencyReadOnly(false);
    setJogId("");
    setJogReadOnly(true);
  };

  const handleInviteAndClose = () => {
    let cleanNumber = phoneNumber;
    if (phoneNumber !== "") {
      cleanNumber = CleanPhoneNumberUtil(phoneNumber);
    }


    dispatch(
      createAndInviteUserRequest(
        email,
        firstName,
        lastName,
        cleanNumber,
        agency,
        jogId,
        userRoles,
        (authState.email ?? "").toLowerCase(),
      )
    );

    props.setInvitedEmail(email);
    setOpen(false);
  };

  const handleAgencySelectionChange = (event: SelectChangeEvent<string>) => {
    const agencyId = event.target.value as string;
    const { jogId, isReadOnly } = getJogId(agencyId);

    setAgency(agencyId);
    setJogId(jogId);
    setJogReadOnly(isReadOnly);
  };

  const handleJogSelectionChanged = (jogId: JogId, elementId: string) => {
    setJogId(jogId);
  };

  const handleRoleSelect = (event: ChangeEvent<HTMLInputElement>) => {
    let newRoles = [...userRoles];
    if (
      event.target.checked === true &&
      newRoles.indexOf(event.target.id) === -1
    ) {
      newRoles.push(event.target.id);
    } else {
      newRoles = newRoles.filter((r) => r !== event.target.id);
    }

    setUserRoles(newRoles);
  };

  return (
    <>
      <Tooltip title="Invite a new user to the VCC">
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpen}
          style={{ minWidth: "150px", color: "white" }}
        >
          Invite User
        </Button>
      </Tooltip>
      <Dialog
        open={open}
        onClose={handleCancel}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Invite User</DialogTitle>
        <DialogContent className="invite-edit-user">
          <DialogContentText>
            Enter an email and user information to invite a new user to the VCC
          </DialogContentText>
          <TextField
            autoFocus
            required
            variant="outlined"
            margin="dense"
            id="email"
            label="Email Address"
            type="email"
            fullWidth
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            required
            variant="outlined"
            margin="dense"
            id="firstName"
            label="First Name"
            type="string"
            fullWidth
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            required
            variant="outlined"
            margin="dense"
            id="lastName"
            label="Last Name"
            type="string"
            fullWidth
            onChange={(e) => setLastName(e.target.value)}
          />
          <TextField
            variant="outlined"
            margin="dense"
            id="phoneNumber"
            label="Phone Number"
            type="phoneNumber"
            fullWidth
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
          <FormControl required fullWidth variant="outlined" margin="dense">
            <InputLabel>Agency</InputLabel>
            <Select
              fullWidth
              value={agency}
              readOnly={agencyReadonly}
              onChange={handleAgencySelectionChange}
              label="Agency"
            >
              {LegacyAgencyGroup.agencies
                .sort((a1, a2) => a1.name > a2.name ? 1 : -1)
                .map((agency) => {
                  if (!agency.isUserAgency) { return undefined; }
                  return <MenuItem key={agency.id} value={agency.id}>{`${agency.name} - (${agency.id})`}</MenuItem>;
                })
              }
            </Select>
          </FormControl>
          <ViewEditSelect
            props={{
              id: "jogId",
              label: "Primary JOG",
              initialValue: jogId !== JogId.Other ? jogId : undefined,
              isEdit: !jogReadonly,
              required: true,
              size: "medium",
              tooltip: "User's primary JOG. Users will receive notifications for incidents created in primary JOG.",
              handleInputChange: handleJogSelectionChanged,
            }}
          >
            {Jogs
              .filter(jog => jog.isUserJog && getAgencyJogIds(agency)?.has(jog.id))
              .sort((j1, j2) => { return (j1.name > j2.name) ? 1 : -1; })
              .map((jog) => {
                return <MenuItem key={jog.id} value={jog.id}>{`${jog.name}`}</MenuItem>;
              })
            }
          </ViewEditSelect>
          <Divider sx={{ margin: "1em 0" }} />
          <FormGroup sx={{ margin: "10px" }}>
            <Box>User Roles</Box>
            <Grid container>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox checked={true} id={UserGroups.VCC_BASIC} />
                  }
                  label={UserGroupLabel.VCC_BASIC}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.VCC_BASIC}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={UserGroups.INC_CONTRIBUTOR}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.INC_CONTRIBUTOR}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.INC_CONTRIBUTOR}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={UserGroups.INC_MANAGER}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.INC_MANAGER}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.INC_MANAGER}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={UserGroups.INC_RECORDS_MANAGER}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.INC_RECORDS_MANAGER}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.INC_RECORDS_MANAGER}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={userRoles.indexOf(UserGroups.PIO) > -1}
                      id={UserGroups.PIO}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.PIO}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.PIO}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={userRoles.indexOf(UserGroups.MAJOR_EVENT_CONTRIBUTOR) > -1}
                      id={UserGroups.MAJOR_EVENT_CONTRIBUTOR}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.MAJOR_EVENT_CONTRIBUTOR}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.MAJOR_EVENT_CONTRIBUTOR}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={UserGroups.AGENCY_ADMIN}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.AGENCY_ADMIN}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.AGENCY_ADMIN}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                {(authState.userGroups.indexOf(UserGroups.SITE_ADMIN) > -1) && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userRoles.indexOf(UserGroups.SITE_ADMIN) > -1}
                        id={UserGroups.SITE_ADMIN}
                        onChange={handleRoleSelect}
                      />
                    }
                    label={UserGroupLabel.SITE_ADMIN}
                  />)}
              </Grid>
            </Grid>
          </FormGroup>
          <DialogActions>
            <Button onClick={handleCancel} color="inherit" variant="contained">
              Cancel
            </Button>
            <Button
              disabled={inviteBtnDisabled}
              onClick={handleInviteAndClose}
              color="primary"
              variant="contained"
            >
              Invite
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InviteUser;
